import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Send, Zap, MessageSquare, ArrowLeft, Circle, AlertTriangle, CheckCircle2, RefreshCw, ChevronRight, FileText, Copy, PenLine, StickyNote } from 'lucide-react';

const channelColors = { email: 'text-[#3B82F6]', whatsapp: 'text-[#10B981]', instagram: 'text-pink-400', facebook: 'text-blue-400', widget: 'text-[#EF694C]', web: 'text-[#a1a1aa]' };
const statusBg = { open: 'bg-[#EF694C]/10 text-[#EF694C] border-[#EF694C]/20', pending: 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20', resolved: 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20', snoozed: 'bg-white/5 text-[#a1a1aa] border-white/10' };
const statusLabels = { open: 'Offen', pending: 'Wartend', resolved: 'Gelöst', snoozed: 'Zurückgestellt' };
const aiValueLabels = {
  intent: { inquiry: 'Anfrage', complaint: 'Beschwerde', support: 'Support', sales: 'Vertrieb', unknown: 'Unklar' },
  category: { billing: 'Abrechnung', technical: 'Technisch', general: 'Allgemein', complaint: 'Beschwerde', inquiry: 'Anfrage' },
  priority: { low: 'Niedrig', medium: 'Mittel', high: 'Hoch', urgent: 'Dringend' },
  sentiment: { positive: 'Positiv', neutral: 'Neutral', negative: 'Negativ', frustrated: 'Frustriert' },
};
const formatAiValue = (type, value) => aiValueLabels[type]?.[value] || value || '—';

const priorityDot = { urgent: 'bg-[#F43F5E]', high: 'bg-[#F59E0B]', medium: 'bg-[#3B82F6]', low: 'bg-[#52525b]' };

const channelLabels = { email: 'E-Mail', whatsapp: 'WhatsApp', instagram: 'Instagram', facebook: 'Facebook', widget: 'Widget', web: 'Web' };
const channelBadge = {
  email: 'bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20',
  whatsapp: 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20',
  instagram: 'bg-pink-400/10 text-pink-300 border-pink-400/20',
  facebook: 'bg-blue-400/10 text-blue-300 border-blue-400/20',
  widget: 'bg-[#EF694C]/10 text-[#EF694C] border-[#EF694C]/20',
  web: 'bg-white/5 text-[#a1a1aa] border-white/10',
};
const priorityLabels = { urgent: 'Dringend', high: 'Hoch', medium: 'Mittel', low: 'Niedrig' };
const priorityBadge = {
  urgent: 'bg-[#F43F5E]/10 text-[#F43F5E] border-[#F43F5E]/20',
  high: 'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20',
  medium: 'bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20',
  low: 'bg-white/5 text-[#a1a1aa] border-white/10',
};
const statusFilterLabels = { all: 'Alle', open: 'Offen', pending: 'Wartend', resolved: 'Gelöst' };

function ConvListItem({ conv, selected, onClick }) {
  return (
    <button onClick={() => onClick(conv)} data-testid={`conv-item-${conv.id}`}
      className={`w-full text-left px-4 py-3.5 border-b border-white/5 transition-colors ${selected ? 'bg-[#EF694C]/10 border-l-2 border-l-[#EF694C]' : 'hover:bg-white/[0.03]'}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-[#EF694C]/15 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-bold text-[#EF694C]">{conv.customer?.name?.charAt(0) || '?'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <p className="text-sm font-medium text-white truncate">{conv.customer?.name || 'Unbekannt'}</p>
            {!conv.is_read && <span className="w-2 h-2 rounded-full bg-[#EF694C] flex-shrink-0" title="Ungelesen" />}
          </div>
          <p className="text-xs text-[#a1a1aa] truncate mt-0.5">{conv.subject || conv.last_message || 'Keine Nachricht'}</p>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${channelBadge[conv.channel] || channelBadge.web}`}>{channelLabels[conv.channel] || conv.channel}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${statusBg[conv.status] || 'bg-white/5 text-[#a1a1aa] border-white/10'}`}>{statusLabels[conv.status] || conv.status}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${priorityBadge[conv.priority] || priorityBadge.low}`}>{priorityLabels[conv.priority] || conv.priority}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function MessageBubble({ msg }) {
  const isCustomer = msg.sender_type === 'customer';
  const isInternal = msg.is_internal;
  const isAI = msg.sender_type === 'ai';

  if (isInternal) return (
    <div className="flex justify-center my-2">
      <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/20 border-l-4 border-l-[#F59E0B] rounded-lg px-4 py-2 max-w-[85%]">
        <p className="text-xs text-[#F59E0B] font-medium mb-0.5 flex items-center gap-1"><StickyNote className="w-3 h-3" /> Interne Notiz — {msg.sender_name}</p>
        <p className="text-sm text-yellow-200/80 italic">{msg.content}</p>
      </div>
    </div>
  );

  return (
    <div className={`flex ${isCustomer ? 'justify-start' : 'justify-end'} mb-3`} data-testid={`message-${msg.id}`}>
      {isCustomer && (
        <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center mr-2 mt-1 flex-shrink-0">
          <span className="text-xs font-bold text-white">{msg.sender_name?.charAt(0) || '?'}</span>
        </div>
      )}
      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${isCustomer ? 'bg-[#121214] border border-white/10 rounded-tl-none' : isAI ? 'bg-[#3B82F6]/10 border border-[#3B82F6]/20 rounded-tr-none' : 'bg-[#EF694C]/10 border border-[#EF694C]/20 rounded-tr-none'}`}>
        <p className={`text-xs font-medium mb-1 ${isCustomer ? 'text-[#a1a1aa]' : isAI ? 'text-[#3B82F6]' : 'text-[#EF694C]'}`}>
          {isAI ? '🤖 KI-Assistent' : msg.sender_name}
        </p>
        <p className="text-sm text-white leading-relaxed">{msg.content}</p>
        <p className="text-xs text-[#52525b] mt-1 font-mono">
          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''}
        </p>
      </div>
    </div>
  );
}

function AIPanel({ conv, messages, tenantId, onUseReply }) {
  const [analyzing, setAnalyzing] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [suggestedReply, setSuggestedReply] = useState(null);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post('/ai/analyze', {
        conversation_id: conv.id, tenant_id: tenantId,
        messages: messages.map(m => ({ content: m.content, sender_type: m.sender_type }))
      });
      setAnalysis(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'KI-Analyse fehlgeschlagen'); }
    finally { setAnalyzing(false); }
  };

  const suggestReply = async () => {
    setSuggesting(true);
    try {
      const res = await api.post('/ai/suggest-reply', {
        conversation_id: conv.id, tenant_id: tenantId,
        messages: messages.map(m => ({ content: m.content, sender_type: m.sender_type }))
      });
      setSuggestedReply(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'KI-Vorschlag fehlgeschlagen'); }
    finally { setSuggesting(false); }
  };

  const sentimentColor = { positive: 'text-[#10B981]', neutral: 'text-[#a1a1aa]', negative: 'text-[#F59E0B]', frustrated: 'text-[#F43F5E]' };
  const priorityC = { urgent: 'text-[#F43F5E]', high: 'text-[#F59E0B]', medium: 'text-[#3B82F6]', low: 'text-[#52525b]' };

  return (
    <div className="w-72 border-l border-white/5 flex flex-col bg-[#0c0c0e] overflow-y-auto">
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-[#EF694C]/10 flex items-center justify-center">
            <Zap className="w-4 h-4 text-[#EF694C]" />
          </div>
          <h3 className="font-heading font-semibold text-sm text-white">KI-Assistent</h3>
          {analysis?._provider && (
            <span className="text-xs text-[#52525b] font-mono ml-auto">{analysis._provider}</span>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={analyze} disabled={analyzing || messages.length === 0} data-testid="ai-analyze-btn"
            className="flex-1 h-8 bg-[#EF694C]/10 hover:bg-[#EF694C]/20 text-[#EF694C] text-xs font-medium rounded-lg border border-[#EF694C]/20 transition-all disabled:opacity-50 flex items-center justify-center gap-1">
            {analyzing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />} Analysieren
          </button>
          <button onClick={suggestReply} disabled={suggesting || messages.length === 0} data-testid="ai-reply-btn"
            className="flex-1 h-8 bg-white/5 hover:bg-white/10 text-[#a1a1aa] hover:text-white text-xs font-medium rounded-lg border border-white/10 transition-all disabled:opacity-50 flex items-center justify-center gap-1">
            {suggesting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <PenLine className="w-3 h-3" />} Vorschlagen
          </button>
        </div>
      </div>

      <div className="flex-1 p-4 space-y-4">
        {analysis && (
          <div className="space-y-3 animate-fade-in-up">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#52525b]">Analyse</p>
            <div className="bg-[#121214] border border-white/10 rounded-lg p-3 space-y-2">
              {[
                { label: 'Absicht', type: 'intent', value: analysis.intent },
                { label: 'Kategorie', type: 'category', value: analysis.category },
                { label: 'Priorität', type: 'priority', value: analysis.priority, className: priorityC[analysis.priority] },
                { label: 'Stimmung', type: 'sentiment', value: analysis.sentiment, className: sentimentColor[analysis.sentiment] },
              ].map(({ label, type, value, className }) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-xs text-[#52525b]">{label}</span>
                  <span className={`text-xs font-medium capitalize ${className || 'text-[#a1a1aa]'}`}>{formatAiValue(type, value)}</span>
                </div>
              ))}
            </div>
            {analysis.summary && (
              <div className="bg-[#121214] border border-white/5 rounded-lg p-3">
                <p className="text-xs font-medium text-[#a1a1aa] mb-1">Zusammenfassung</p>
                <p className="text-xs text-white leading-relaxed">{analysis.summary}</p>
              </div>
            )}
            {analysis.kb_answer && (
              <div className="bg-[#3B82F6]/5 border border-[#3B82F6]/10 rounded-lg p-3">
                <p className="text-xs font-medium text-[#3B82F6] mb-1 flex items-center gap-1">
                  <FileText className="w-3 h-3" /> KB-Antwort
                </p>
                <p className="text-xs text-white leading-relaxed">{analysis.kb_answer}</p>
              </div>
            )}
            <div className="flex items-center justify-between bg-[#121214] border border-white/5 rounded-lg p-3">
              <span className="text-xs text-[#52525b]">Sicherheit</span>
              <div className="flex items-center gap-2">
                <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-[#EF694C] rounded-full" style={{ width: `${(analysis.confidence || 0) * 100}%` }} />
                </div>
                <span className="text-xs font-mono text-[#a1a1aa]">{Math.round((analysis.confidence || 0) * 100)}%</span>
              </div>
            </div>
            {analysis.escalate && (
              <div className="flex items-center gap-2 p-2.5 bg-[#F43F5E]/10 border border-[#F43F5E]/20 rounded-lg" data-testid="escalation-alert">
                <AlertTriangle className="w-4 h-4 text-[#F43F5E] flex-shrink-0" />
                <p className="text-xs text-[#F43F5E]">Eskalation empfohlen</p>
              </div>
            )}
            {/* Citations from analysis */}
            {analysis._citations && analysis._citations.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#52525b]">Quellen</p>
                {analysis._citations.slice(0, 3).map((c, i) => (
                  <div key={i} className="bg-[#121214] border border-white/5 rounded-lg p-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-mono text-[#3B82F6]">Quelle {c.index}</span>
                      <span className="text-xs font-mono text-[#52525b]">{(c.score * 100).toFixed(0)}%</span>
                    </div>
                    <p className="text-xs text-[#a1a1aa] line-clamp-2">{c.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {suggestedReply && (
          <div className="space-y-2 animate-fade-in-up">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#52525b]">Antwortvorschlag</p>
            {suggestedReply.grounded ? (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-[#10B981]/10 border border-[#10B981]/20 rounded-md" data-testid="grounded-badge">
                <FileText className="w-3 h-3 text-[#10B981]" />
                <span className="text-xs text-[#10B981] font-medium">Durch Wissensdatenbank gestützte Antwort</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-md" data-testid="not-grounded-badge">
                <AlertTriangle className="w-3 h-3 text-[#F59E0B]" />
                <span className="text-xs text-[#F59E0B] font-medium">Allgemeine Antwort ohne Wissensdatenbank</span>
              </div>
            )}
            <div className="bg-[#121214] border border-[#EF694C]/20 rounded-lg p-3">
              <p className="text-xs text-white leading-relaxed whitespace-pre-wrap">{suggestedReply.reply}</p>
              {suggestedReply.confidence != null && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/5">
                  <span className="text-xs text-[#52525b]">Sicherheit:</span>
                  <div className="w-12 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-[#EF694C] rounded-full" style={{ width: `${(suggestedReply.confidence || 0) * 100}%` }} />
                  </div>
                  <span className="text-xs font-mono text-[#a1a1aa]">{Math.round((suggestedReply.confidence || 0) * 100)}%</span>
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <button onClick={() => { navigator.clipboard.writeText(suggestedReply.reply); toast.success('Kopiert!'); }}
                  data-testid="copy-reply-btn"
                  className="flex-1 h-7 bg-white/5 hover:bg-white/10 text-[#a1a1aa] text-xs rounded border border-white/10 transition-all flex items-center justify-center gap-1">
                  <Copy className="w-3 h-3" /> Kopieren
                </button>
                <button onClick={() => onUseReply(suggestedReply.reply)} data-testid="use-reply-btn"
                  className="flex-1 h-7 bg-[#EF694C]/10 hover:bg-[#EF694C]/20 text-[#EF694C] text-xs rounded border border-[#EF694C]/20 transition-all flex items-center justify-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Übernehmen
                </button>
              </div>
            </div>
            {/* Citations from suggested reply */}
            {suggestedReply.citations && suggestedReply.citations.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#52525b]">Genutzte Quellen</p>
                {suggestedReply.citations.slice(0, 3).map((c, i) => (
                  <div key={i} className="bg-[#121214] border border-white/5 rounded-lg p-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-mono text-[#3B82F6]">Quelle {c.index}</span>
                      <span className="text-xs font-mono text-[#52525b]">{(c.score * 100).toFixed(0)}%</span>
                    </div>
                    <p className="text-xs text-[#a1a1aa] line-clamp-2">{c.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!analysis && !suggestedReply && (
          <div className="text-center py-8">
            <Zap className="w-8 h-8 text-[#52525b] mx-auto mb-2" />
            <p className="text-xs text-[#52525b]">Klicken Sie auf Analysieren oder Vorschlagen, um KI-Unterstützung zu erhalten</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function InboxPage() {
  const { user } = useAuth();
  const location = useLocation();
  const tenantId = user?.tenant_id;
  const messagesEndRef = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showAI, setShowAI] = useState(true);
  const [showMobileConv, setShowMobileConv] = useState(false);

  const fetchConversations = async () => {
    if (!tenantId) return;
    try {
      const res = await api.get(`/tenants/${tenantId}/conversations?status=${statusFilter}&limit=50`);
      setConversations(res.data.conversations || []);
    } catch { toast.error('Unterhaltungen konnten nicht geladen werden'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchConversations(); }, [tenantId, statusFilter]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectConv = async (conv) => {
    setSelected(conv);
    setShowMobileConv(true);
    setMsgLoading(true);
    try {
      const res = await api.get(`/tenants/${tenantId}/conversations/${conv.id}/messages`);
      setMessages(res.data || []);
      if (!conv.is_read) {
        await api.patch(`/tenants/${tenantId}/conversations/${conv.id}`, { is_read: true });
        setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, is_read: true } : c));
      }
    } catch { toast.error('Failed to load messages'); }
    finally { setMsgLoading(false); }
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !selected) return;
    setSending(true);
    try {
      const res = await api.post(`/tenants/${tenantId}/conversations/${selected.id}/messages`, {
        content: newMsg.trim(), is_internal: isInternal, sender_type: 'agent'
      });
      setMessages(prev => [...prev, res.data]);
      setNewMsg('');
      setIsInternal(false);
      setConversations(prev => prev.map(c => c.id === selected.id ? { ...c, last_message: newMsg.trim().slice(0, 80), message_count: c.message_count + 1 } : c));
    } catch { toast.error('Nachricht konnte nicht gesendet werden'); }
    finally { setSending(false); }
  };

  const updateStatus = async (status) => {
    if (!selected) return;
    try {
      const updated = await api.patch(`/tenants/${tenantId}/conversations/${selected.id}`, { status });
      setSelected(updated.data);
      setConversations(prev => prev.map(c => c.id === selected.id ? updated.data : c));
      toast.success(status === 'resolved' ? 'Als gelöst markiert' : status === 'open' ? 'Wieder geöffnet' : 'Status aktualisiert');
    } catch { toast.error('Status konnte nicht aktualisiert werden'); }
  };

  const filtered = conversations.filter(c =>
    c.customer?.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.subject?.toLowerCase().includes(search.toLowerCase()) ||
    c.last_message?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex h-[calc(100vh-64px)] bg-[#09090b]">
      {/* Left: Conversation List */}
      <div className={`${showMobileConv ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 xl:w-96 flex-shrink-0 border-r border-white/5`}>
        {/* Search */}
        <div className="p-3 border-b border-white/5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525b]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Unterhaltungen durchsuchen..."
              data-testid="inbox-search"
              className="w-full h-9 bg-[#121214] border border-white/10 rounded-lg pl-9 pr-3 text-white text-sm placeholder-[#52525b] focus:outline-none focus:border-[#EF694C] transition-all" />
          </div>
        </div>
        {/* Status Filters */}
        <div className="flex gap-1 p-2 border-b border-white/5">
          {['all', 'open', 'pending', 'resolved'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`inbox-filter-${s}`}
              className={`flex-1 h-7 text-xs font-medium rounded-md transition-all ${statusFilter === s ? 'bg-[#EF694C]/10 text-[#EF694C]' : 'text-[#52525b] hover:text-[#a1a1aa] hover:bg-white/5'}`}>
              {statusFilterLabels[s] || s}
            </button>
          ))}
        </div>
        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-7 h-7 border-2 border-[#EF694C]/30 border-t-[#EF694C] rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare className="w-10 h-10 text-[#52525b] mx-auto mb-2" />
              <p className="text-sm text-[#52525b]">Keine Unterhaltungen</p>
            </div>
          ) : (
            filtered.map(c => <ConvListItem key={c.id} conv={c} selected={selected?.id === c.id} onClick={selectConv} />)
          )}
        </div>
      </div>

      {/* Right: Conversation Detail */}
      <div className={`${!showMobileConv ? 'hidden md:flex' : 'flex'} flex-1 flex-col min-w-0`}>
        {selected ? (
          <>
            {/* Header */}
            <div className="h-14 flex items-center justify-between px-4 border-b border-white/5 bg-[#0c0c0e] flex-shrink-0">
              <div className="flex items-center gap-3">
                <button className="md:hidden p-1 text-[#a1a1aa]" onClick={() => setShowMobileConv(false)}>
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="w-8 h-8 rounded-full bg-[#EF694C]/10 flex items-center justify-center">
                  <span className="text-xs font-bold text-[#EF694C]">{selected.customer?.name?.charAt(0)}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{selected.customer?.name}</p>
                  <p className="text-xs text-[#52525b]">{selected.customer?.email || selected.channel}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded border ${statusBg[selected.status] || 'bg-white/5 text-[#a1a1aa] border-white/10'}`}>
                  {statusLabels[selected.status] || selected.status}
                </span>
                {selected.status === 'open' && (
                  <button onClick={() => updateStatus('resolved')} data-testid="resolve-btn"
                    className="h-7 px-3 text-xs font-medium bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20 rounded-lg hover:bg-[#10B981]/20 transition-all flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Als gelöst markieren
                  </button>
                )}
                {selected.status === 'resolved' && (
                  <button onClick={() => updateStatus('open')} data-testid="reopen-btn"
                    className="h-7 px-3 text-xs font-medium bg-[#EF694C]/10 text-[#EF694C] border border-[#EF694C]/20 rounded-lg hover:bg-[#EF694C]/20 transition-all">
                    Wieder öffnen
                  </button>
                )}
                <button onClick={() => setShowAI(v => !v)} data-testid="toggle-ai-panel"
                  className={`h-7 w-7 flex items-center justify-center rounded-lg border transition-all ${showAI ? 'bg-[#EF694C]/10 text-[#EF694C] border-[#EF694C]/20' : 'text-[#a1a1aa] border-white/10 hover:bg-white/5'}`}>
                  <Zap className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex flex-1 min-h-0 overflow-hidden">
              {/* Messages */}
              <div className="flex-1 flex flex-col min-w-0">
                <div className="flex-1 overflow-y-auto p-4">
                  {msgLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="w-7 h-7 border-2 border-[#EF694C]/30 border-t-[#EF694C] rounded-full animate-spin" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-16 text-[#52525b]">Noch keine Nachrichten</div>
                  ) : (
                    messages.map(m => <MessageBubble key={m.id} msg={m} />)
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Composer */}
                <div className="border-t border-white/5 p-3 bg-[#0c0c0e] flex-shrink-0">
                  <div className={`rounded-xl border transition-all ${isInternal ? 'border-[#F59E0B]/30 bg-[#F59E0B]/5' : 'border-white/10 bg-[#121214]'}`}>
                    <textarea
                      value={newMsg}
                      onChange={e => setNewMsg(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendMessage(); }}
                      placeholder={isInternal ? "Interne Notiz schreiben... (nur für Mitarbeiter sichtbar)" : "Antwort schreiben... (Strg+Enter zum Senden)"}
                      rows={3}
                      data-testid="message-composer"
                      className="w-full bg-transparent px-4 pt-3 pb-2 text-sm text-white placeholder-[#52525b] resize-none focus:outline-none"
                    />
                    <div className="flex items-center justify-between px-3 pb-2.5">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setIsInternal(v => !v)} data-testid="toggle-internal"
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-all ${isInternal ? 'bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/20' : 'text-[#52525b] border-white/10 hover:bg-white/5 hover:text-[#a1a1aa]'}`}>
                          <StickyNote className="w-3 h-3" /> {isInternal ? 'Interne Notiz' : 'Antwort'}
                        </button>
                      </div>
                      <button onClick={sendMessage} disabled={!newMsg.trim() || sending} data-testid="send-message-btn"
                        className="flex items-center gap-1.5 h-8 px-4 bg-[#EF694C] hover:bg-[#d65d43] text-white text-xs font-semibold rounded-lg shadow-[0_0_10px_rgba(239,105,76,0.3)] transition-all active:scale-95 disabled:opacity-50">
                        {sending ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        {isInternal ? 'Notiz hinzufügen' : 'Senden'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Panel */}
              {showAI && (
                <AIPanel conv={selected} messages={messages} tenantId={tenantId}
                  onUseReply={(reply) => setNewMsg(reply)} />
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center flex-1 text-center p-8">
            <div className="w-16 h-16 rounded-2xl bg-[#EF694C]/10 flex items-center justify-center mb-4">
              <MessageSquare className="w-8 h-8 text-[#EF694C]" />
            </div>
            <h3 className="font-heading font-semibold text-lg text-white mb-2">Unterhaltung auswählen</h3>
            <p className="text-[#a1a1aa] text-sm max-w-xs">Wählen Sie links eine Unterhaltung aus, um zu antworten.</p>
          </div>
        )}
      </div>
    </div>
  );
}
